/* eslint-disable max-lines */
import { favoritesAssignWidgetReducer, IFavoritesAssignWidgetState } from '@cian/favorites-assign-widget';
import { combineReducers, configureStore, Reducer, Store, ThunkAction, ThunkDispatch } from '@reduxjs/toolkit';

import { TMortgageType } from 'node/utils/getMortgageType';

import { EBannerType } from 'shared/constants/banner';
import { bestPlaceModalReducer } from 'shared/reducers/bestPlaceReportModal/reducer';
import { buildersProjectsLocationsReducer } from 'shared/reducers/buildersProjectsLocations';
import { chatOnboardingReducer } from 'shared/reducers/chatOnboarding';
import { comparisonReducer } from 'shared/reducers/comparison';
import { consultantCianChatReducer, IConsultantCianChatsState } from 'shared/reducers/consultantCianChat';
import { cpdReducer } from 'shared/reducers/cpd';
import { dailyrentActionTypeReducer, TDailyrentActionTypeState } from 'shared/reducers/dailyrentActionType';
import { dailyrentBookingCalendarReducer } from 'shared/reducers/dailyrentBookingCalendar/reducer';
import { dailyrentBookingGeneralReducer } from 'shared/reducers/dailyrentBookingGeneral/reducer';
import { dailyrentBookingGuestsReducer } from 'shared/reducers/dailyrentBookingGuests/reducer';
import { newbuildingBrokerOfferBookingReducer } from 'shared/reducers/newbuildingBrokerOfferBooking';
import { newbuildingExtraPricesReducer } from 'shared/reducers/newbuildingExtraPrices';
import { reviewsGoToAppModalReducer } from 'shared/reducers/reviewsGoToAppModal';
import { valuationTrapsReducer } from 'shared/reducers/valuationTraps';
import { IDailyrentBookingState } from 'shared/store/dailyrentBookingParams/types';
import { IValuationTrapsState } from 'shared/store/valuationTraps';
import { IBestPlaceAnalyticsState } from 'shared/types/bestPlaceAnalytics';
import { IBestPlaceReportModalState } from 'shared/types/bestPlaceReportModal';
import { IChatOnboarding } from 'shared/types/chatOnboarding';
import { ICPDState } from 'shared/types/cpd/cpd';
import { EInfrastructureCategory } from 'shared/types/infrastructure';
import { INewbuildingBrokerOfferBooking } from 'shared/types/newbuildingBrokerOfferBooking';
import { IPurchaseTermsResponse } from 'shared/types/purchaseTerm/purchaseTermsV2';
import { IReviewsGoToAppModalState } from 'shared/types/reviewsGoToAppModal';
import { ISubscriptionPriceChangesPopup } from 'shared/types/subscriptionPriceChangesPopup';
import { ISuburbanProjectSuggest } from 'shared/types/suburbanBuildersProjects';

import { abUseExperimentsReducer } from './abUseExperiments';
import { adfoxOfferReducer } from './adfoxParams/reducer/adfoxOfferReducer';
import { banksListReducer } from './banksList';
import { commentsReducer, IComments } from './comments';
import { IСomparisonState } from './comparison';
import { complaintPopupOpenReducer } from './complaintPopupOpen';
import { IState as IDealRentState, reducer as dealRent } from './deal-rent';
import { earlyAccessCounterReducer, IEarlyAccessCounterState } from './earlyAccessCounter';
import { ebcReducer, IEbcState } from './ebc';
import { etazhiBlackFridayReducer, IEtazhiBlackFridayState } from './etazhiBlackFriday';
import { favoriteMotivationReducer, IFavoriteMotivationState } from './favoriteMotivation';
import { galleryReducer, IGalleryData } from './gallery/';
import { genplanReducer } from './genplan';
import { hideOfferReducer, IHideOffer } from './hideOffer';
import { homeownerReducer } from './homeowner';
import { infrastructureCategoriesReducer } from './infrastructure-categories';
import { infrastructureMapReducer, TInfrastructureMapState } from './infrastructure-map';
import { knAuctionCompaniesReducer } from './knAuctionCompanies/reducer';
import { ISimilarOffersData, IViewedData, mapSimilarReducer, markViewedReducer } from './map_similar';
import { IMapTabs, mapTabsReducer } from './map_tabs';
import { IMortgageState, mortgageStateReducer } from './mortgage';
import { IMortgageGalleryState, mortgageGalleryStateReducer } from './mortgageGallery';
import { asideReducer, IAside } from './navigation';
import { notAvailablePopupOpenReducer } from './notAvailablePopupOpen';
import { offerReducer } from './offer';
import { comparisonNotificationReducer } from './offerComparisonNotifications';
import { IOffersHistoryState } from './offerHistory';
import { IOfferValuationState } from './offerValuation';
import { offerValuationReducer } from './offerValuation/reducer';
import { IOfferStatsPopupState, offerStatsPopupReducer } from './offer_stats_popup';
import { phoneCollapseReducer } from './phone_collapse';
import { ILegendState, legendModalReducer } from './popups/legend';
import { IPrintModal, printModalReducer } from './popups/print';
import { IReclaimModalState, reclaimModalReducer } from './popups/reclaim';
import { IRemoveState, removeModalReducer } from './popups/remove';
import { pushSubscribedReducer, TPushSubscribed } from './push_subscription';
import { recommendedVillagesReducer } from './recommendedVillages';
import { IState as IRenterFormState, reducer as renterForm } from './renterForm';
import { rosreestrReducer } from './rosreestr';
import { rsReducer } from './rs/reducer';
import { IRSState } from './rs/types';
import { ISeoLinksState, seoLinksReducer } from './seo_links';
import { ISimilarState, similarOffersReducer } from './similar';
import { specialPromoInfoReducer, TSpecialPromoInfoState } from './special_promo_info';
import { subscriptionPriceChangesPopupReducer } from './subscription_price_changes_popup';
import { suburbanBuildersProjectsReducer } from './suburbanBuildersProjects';
import { ITrackingReducerState, trackingReducer } from './tracking';
import { userDataReducer } from './user';
import { villagePromotionsReducer } from './villagePromotions';
import { IWidgetsMarkupState, widgetMarkupReducer } from './widgetsMarkup/reducers';
import { feedbackReducer, IFeedback } from '../FeedbackExperiment/reducers/feedback';
import { bannerRotationReducer } from '../reducers/bannersRotation';
import { bestPlaceAnalyticsReducer } from '../reducers/bestPlaceAnalytics';
import { bookedOfferRecommendationsReducer } from '../reducers/bookedOfferRecommendations';
import { buyAnalyticsModalReducer } from '../reducers/buyAnalyticsModal';
import { chatReducer, IOfferChatsState } from '../reducers/chat';
import { flatTourBookingReducer } from '../reducers/flatTourBookingReducer';
import { newbuildingBookedFromDeveloperReducer } from '../reducers/newbuildingBookedFromDeveloper';
import { newbuildingBookedOfferLayoutsReducer } from '../reducers/newbuildingBookedOfferLayouts';
import { offersHistoryReducer } from '../reducers/offersHistory';
import { phoneWarningReducer } from '../reducers/phoneWarning';
import { IGenplanScheme } from '../types';
import { IAbUseExperiment } from '../types/abUseExperiments';
import { TAdfoxRequestedOffer } from '../types/adfox';
import { IApplicationContext } from '../types/applicationContext';
import { IBankListState } from '../types/banksList';
import { IBuyAnalyticsModalState } from '../types/buyAnalyticsModal';
import { IComparisonNotification } from '../types/comparisonNotification';
import { IFlatTourBookingState } from '../types/flatTourBooking';
import { INewbuildingBookedFromDeveloperState } from '../types/newbuildingBookedFromDeveloper';
import { INewbuildingLayoutOffer } from '../types/newbuildingBookedOfferLayouts';
import { IOfferData } from '../types/offerData';
import { IPhoneWarningState } from '../types/phoneWarning';
import { IRecomendedOfferSchema } from '../types/recommendedOffer';
import { IRecommendedVillages } from '../types/recommendedVillages';
import { IRosreestrState } from '../types/rosreestr';
import { IVillagePromotionsData } from '../types/villagePromotions';

import type { RootAction } from '../types/redux/RootAction';

export interface IPageData {
  subdomain: string;
  dealType: 'sale' | 'rent';
  offerType: 'flat' | 'suburban' | 'commercial';
  offerId: number;
}

export interface IUtilitiesTermsSettings {
  isUtilitiesTermsEnabled: boolean;
  useIncludeUtilitiesTermsRegions: boolean;
  excludeUtilitiesTermsRegions: number[];
  includeUtilitiesTermsRegions: number[];
}

export interface IFeatureToggleState {
  /** Настройки коммунальных услуг в аренде жилой */
  utilitiesTermsSettings: IUtilitiesTermsSettings;
  isFavoritesFolderEnabled: boolean;
  /** Флаг показа кнопки покупки аналитического отчета по зданию */
  isBuyAnalyticsButtonEnabled: boolean;
  /** Флаг показа ссылки на оферту для покупки аналитического отчета по зданию */
  isLegalDocsUrlForCommercialReportsEnabled: boolean;
  isCommercialNewVatTextEnabled: boolean;
  /** Баннер сервиса "Ипотечный брокер" (Циан.Ипотека для профи) включен? */
  isMortgageBrokerBannerEnabled: boolean;
  bannerRotation?: EBannerType;
  /** Включен чекаут BestPlace при покупке отчета по помещению */
  isBestPlaceCheckoutEnabled: boolean;
  isChatOnboardingEnabled: boolean;
  /**
   * @description Новый микрофронтенд ипотечного калькулятора TDSC-392
   */
  isMortgageCalculatorInObjectCardEnabled: boolean;
  isMortgageCalculatorInSecondaryObjectCardEnabled: boolean;
  isMortgageCalculatorInSuburbanCardEnabled: boolean;
  /** Способы оплаты новостроек */
  isPurchaseTermsEnabled: boolean;
  /** Флоу просмотра контактов объектов в раннем доступе */
  earlyAccessContactsFlowEnabled: boolean;
  isNewbuildingBrokerFlatBannerEnabled: boolean;
}

export interface IState {
  abGroup: number;
  abUseExperiments: IAbUseExperiment[];
  aside: IAside;
  captchaSiteKey: string;
  captchaSpamSiteKey: string;
  commentsData: IComments;
  complaintPopupOpen: boolean;
  notAvailablePopupOpen: boolean;
  favoriteMotivation: IFavoriteMotivationState;
  feedback: IFeedback;
  gallery: IGalleryData;
  hideOffer: IHideOffer;
  isStartChatWidgetInDropdown: boolean;
  isCountrysideOffersBuilderBadgeEnabled: boolean;
  legend: ILegendState;
  mapSimilar: ISimilarOffersData;
  mapTabs: IMapTabs;
  mortgage: IMortgageState;
  mortgageGallery: IMortgageGalleryState;
  offerChat: IOfferChatsState;
  consultantCianChat: IConsultantCianChatsState;
  offerData: IOfferData;
  promotionsVillageData: IVillagePromotionsData;
  offerStatsPopup: IOfferStatsPopupState;
  pageData: IPageData;
  phoneCollapse: boolean;
  phoneWarning: IPhoneWarningState;
  printModal: IPrintModal;
  reclaimModal: IReclaimModalState;
  remove: IRemoveState;
  seoLinks: ISeoLinksState;
  serviceBaseUrl: string;
  similarOffers: ISimilarState;
  specialPromoInfo: TSpecialPromoInfoState;
  subscriptionPriceChangesPopup: ISubscriptionPriceChangesPopup;
  tracking: ITrackingReducerState;
  viewedSimilar: IViewedData;
  pushSubscribed: TPushSubscribed;
  newbuildingBookedFromDeveloper: INewbuildingBookedFromDeveloperState;
  ebcInfo: IEbcState;
  /** Флаг для выкатки брэндирования */
  isBrandingAvailable: boolean;
  /** Перечень регионов ранжированного поиска */
  mlSearchRegions?: number[] | null;
  /** Флаг активности ML-выдачи для всех */
  isMlSearchForAllEnabled: boolean;
  /** Флаг для demand - является ли юзер собственником **/
  isHomeowner: boolean;
  soprApi: string;
  widgetsMarkup: IWidgetsMarkupState;
  infrastructureMap: TInfrastructureMapState;
  isCountrysidePromoEnabled: boolean;
  /** Списк publisherUserId объявлений, публикации которых учавствуют аукционах **/
  knAuctionCompanies: number[];
  auctionBanks: number[];
  dealRent: IDealRentState;
  renterForm: IRenterFormState;
  featureToggle: IFeatureToggleState;
  dealRentVasCost: number;
  isHomeownerDocsRequired: boolean;
  offersHistory: IOffersHistoryState;
  offerValuation: IOfferValuationState;
  /** Показываем кнопку аналитики  */
  showAnalyticsButton: boolean;
  entryToShowEnabled: boolean;
  favoritesAssignWidget: IFavoritesAssignWidgetState;
  banksList: IBankListState;
  mortgagePromoDesign: string | null;
  flatTourBooking: IFlatTourBookingState;
  crossedOutPriceExcludeNewbuildingIds: number[];
  comparisonNotification: IComparisonNotification | null;
  isQaMode: boolean;
  rs: IRSState;
  isRenterFormEnabled: boolean;
  isScrollToCalculator: boolean;
  mortgageType: TMortgageType | null;
  infrastructureCategories: EInfrastructureCategory[];
  buyAnalyticsModal: IBuyAnalyticsModalState;
  bestPlaceModal: IBestPlaceReportModalState;
  recommendedVillages: IRecommendedVillages | null;
  genplan: IGenplanScheme | null;
  adfoxOffer: TAdfoxRequestedOffer | null;
  bookedOfferId: number;
  rosreestr: IRosreestrState | null;
  bestPlaceAnalytics: IBestPlaceAnalyticsState;
  buildersProjectsLocations: number[];
  suburbanBuildersProjects: ISuburbanProjectSuggest[];
  reviewsGoToAppModal: IReviewsGoToAppModalState;
  valuationTraps: IValuationTrapsState;
  cpd?: ICPDState;
  dailyrentBooking: IDailyrentBookingState;
  dailyrentActionType: TDailyrentActionTypeState;
  newStatsEnabled: boolean;
  bannerRotation: EBannerType;
  comparison: IСomparisonState;
  // TODO: Удалить в CD-226224
  chatOnboarding: IChatOnboarding;
  newbuildingBrokerOfferBooking: INewbuildingBrokerOfferBooking;
  newbuildingExtraPrices: IPurchaseTermsResponse;
  newbuildingBookedOfferLayouts: INewbuildingLayoutOffer[];
  bookedOfferRecommendations: IRecomendedOfferSchema[];
  earlyAccessCounter: IEarlyAccessCounterState;

  /**
   * Код появился к проекту "Чёрная пятница" для компании "Этажи".
   * @todo: удалить в задаче CD-251228
   */
  etazhiBlackFriday: IEtazhiBlackFridayState;
}

export type DispatchReduxAction = ThunkDispatch<IState, IApplicationContext, RootAction>;

export type IThunkActionCreator<T = void> = ThunkAction<T, IState, IApplicationContext, RootAction>;

let storeReminder: Store<IState>;

export function createStore(
  context: IApplicationContext,
  preloadedState: IState,
  isBrowser: boolean = false,
): Store<IState, RootAction> {
  const rootReducer = combineReducers<IState, RootAction>({
    abGroup: (state: IState['abGroup'] = 0) => state,
    abUseExperiments: abUseExperimentsReducer,
    aside: asideReducer,
    hideOffer: hideOfferReducer,
    bookedOfferId: (state: IState['bookedOfferId'] = 0) => state,
    captchaSiteKey: (state: IState['captchaSiteKey'] = '') => state,
    captchaSpamSiteKey: (state: IState['captchaSpamSiteKey'] = '') => state,
    commentsData: commentsReducer,
    favoriteMotivation: favoriteMotivationReducer,
    feedback: feedbackReducer,
    gallery: galleryReducer,
    legend: legendModalReducer,
    mapSimilar: mapSimilarReducer,
    mapTabs: mapTabsReducer,
    mortgage: mortgageStateReducer,
    mortgageGallery: mortgageGalleryStateReducer,
    newbuildingBookedFromDeveloper: newbuildingBookedFromDeveloperReducer,
    offerChat: chatReducer,
    consultantCianChat: consultantCianChatReducer,
    offerData: reduceReducers<IState['offerData']>(offerReducer, userDataReducer),
    promotionsVillageData: villagePromotionsReducer,
    offerStatsPopup: offerStatsPopupReducer,
    pageData: (state: IState['pageData'] = {} as IState['pageData']) => state,
    phoneCollapse: phoneCollapseReducer,
    phoneWarning: phoneWarningReducer,
    printModal: printModalReducer,
    pushSubscribed: pushSubscribedReducer,
    reclaimModal: reclaimModalReducer,
    remove: removeModalReducer,
    seoLinks: seoLinksReducer,
    serviceBaseUrl: (state: IState['serviceBaseUrl'] = '') => state,
    similarOffers: similarOffersReducer,
    specialPromoInfo: specialPromoInfoReducer,
    subscriptionPriceChangesPopup: subscriptionPriceChangesPopupReducer,
    tracking: trackingReducer,
    viewedSimilar: markViewedReducer,
    ebcInfo: ebcReducer,
    mlSearchRegions: (state: IState['mlSearchRegions'] = null) => state,
    isMlSearchForAllEnabled: (state: IState['isMlSearchForAllEnabled'] = false) => state,
    isBrandingAvailable: (state: IState['isBrandingAvailable'] = false) => state,
    isCountrysideOffersBuilderBadgeEnabled: (state: IState['isCountrysideOffersBuilderBadgeEnabled'] = false) => state,
    isHomeowner: homeownerReducer,
    soprApi: (state: IState['soprApi'] = '') => state,
    widgetsMarkup: widgetMarkupReducer,
    infrastructureMap: infrastructureMapReducer,
    infrastructureCategories: infrastructureCategoriesReducer,
    isCountrysidePromoEnabled: (state: IState['isCountrysidePromoEnabled'] = false) => state,
    knAuctionCompanies: knAuctionCompaniesReducer,
    auctionBanks: (state: IState['auctionBanks'] = Array.of<number>()) => state,
    dealRent,
    renterForm,
    complaintPopupOpen: complaintPopupOpenReducer,
    notAvailablePopupOpen: notAvailablePopupOpenReducer,
    featureToggle: (state: IState['featureToggle'] = {} as IState['featureToggle']) => state,
    dealRentVasCost: (state: IState['dealRentVasCost'] = 0) => state,
    isHomeownerDocsRequired: (state: IState['isHomeownerDocsRequired'] = false) => state,
    offersHistory: offersHistoryReducer,
    offerValuation: offerValuationReducer,
    showAnalyticsButton: (state: IState['showAnalyticsButton'] = false) => state,
    entryToShowEnabled: (state: IState['entryToShowEnabled'] = false) => state,
    newStatsEnabled: (state: IState['newStatsEnabled'] = false) => state,
    favoritesAssignWidget: favoritesAssignWidgetReducer,
    banksList: banksListReducer,
    mortgagePromoDesign: (state = null) => state,
    flatTourBooking: flatTourBookingReducer,
    crossedOutPriceExcludeNewbuildingIds: (
      state: IState['crossedOutPriceExcludeNewbuildingIds'] = Array.of<number>(),
    ) => state,
    comparisonNotification: comparisonNotificationReducer,
    rs: rsReducer,
    isQaMode: (state: IState['isQaMode'] = false) => state,
    isRenterFormEnabled: (state: IState['isRenterFormEnabled'] = false) => state,
    isScrollToCalculator: (state: IState['isScrollToCalculator'] = false) => state,
    mortgageType: (state: IState['mortgageType'] = null) => state,
    buyAnalyticsModal: buyAnalyticsModalReducer,
    bestPlaceModal: bestPlaceModalReducer,
    recommendedVillages: recommendedVillagesReducer,
    genplan: genplanReducer,
    adfoxOffer: adfoxOfferReducer,
    isStartChatWidgetInDropdown: (state: IState['isStartChatWidgetInDropdown'] = false) => state,
    rosreestr: rosreestrReducer,
    bestPlaceAnalytics: bestPlaceAnalyticsReducer,
    buildersProjectsLocations: buildersProjectsLocationsReducer,
    suburbanBuildersProjects: suburbanBuildersProjectsReducer,
    reviewsGoToAppModal: reviewsGoToAppModalReducer,
    valuationTraps: valuationTrapsReducer,
    cpd: cpdReducer,
    dailyrentBooking: combineReducers<IDailyrentBookingState, RootAction>({
      general: dailyrentBookingGeneralReducer,
      calendar: dailyrentBookingCalendarReducer,
      guests: dailyrentBookingGuestsReducer,
    }),
    dailyrentActionType: dailyrentActionTypeReducer,
    bannerRotation: bannerRotationReducer,
    comparison: comparisonReducer,
    chatOnboarding: chatOnboardingReducer,
    newbuildingBrokerOfferBooking: newbuildingBrokerOfferBookingReducer,
    newbuildingExtraPrices: newbuildingExtraPricesReducer,
    newbuildingBookedOfferLayouts: newbuildingBookedOfferLayoutsReducer,
    bookedOfferRecommendations: bookedOfferRecommendationsReducer,
    earlyAccessCounter: earlyAccessCounterReducer,

    /**
     * Код появился к проекту "Чёрная пятница" для компании "Этажи".
     * @todo: удалить в задаче CD-251228
     */
    etazhiBlackFriday: etazhiBlackFridayReducer,
  });

  const projectName = context.config.getStrict<string>('projectName');

  const createReduxStore = () =>
    configureStore({
      devTools: isBrowser ? { name: projectName } : false,
      middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
          thunk: { extraArgument: context },
          /**
           * При локальной разработке (в т.ч. в тестах) у некоторых карточек все падает
           * из-за ошибки мутации стейта, сейчас это достаточно сложно поправить
           *
           * Временное решение: https://stackoverflow.com/questions/64695464/error-invariant-failed-a-state-mutation-was-detected-between-dispatches-in-th
           */
          ...(process.env.NODE_ENV === 'development' ? { immutableCheck: false, serializableCheck: false } : {}),
        }),
      preloadedState,
      reducer: rootReducer,
    });

  if (process.env.NODE_ENV === 'development') {
    if (isBrowser) {
      if (!storeReminder) {
        storeReminder = createReduxStore();

        return storeReminder;
      }

      storeReminder.replaceReducer(rootReducer);

      return storeReminder;
    }
  }

  return createReduxStore();
}

function reduceReducers<S>(...reducers: Reducer<S>[]) {
  return (prevState: S, action: RootAction) =>
    reducers.reduce((newState, reducer) => reducer(newState, action), prevState);
}
